/**
 * App.tsx
 * Point d'entrée React de l'application Snapbooth Template Editor
 * Architecture SaaS avec react-konva et Zustand
 */
import React, { useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectService } from './services/ProjectService';
import { ErrorBoundary } from './components/react/common/ErrorBoundary';
import { ErrorState } from './components/react/common/ErrorState';
import { ToastProvider, useToast } from './components/react/common/ToastProvider';
import { ExportErrorListener } from './components/react/common/ExportErrorListener';
import { CanvasArea } from './components/react/Canvas/CanvasArea';
import { ModernSidebar, DrawerContent } from './components/react/Sidebar/ModernSidebar';
import { PropertiesPanel } from './components/react/PropertiesPanel/PropertiesPanel';
import { SnapboothTopbar } from './components/react/Topbar/SnapboothTopbar';
import { BottomBar } from './components/react/BottomBar/BottomBar';
import { LazySuspense } from './components/react/common/LazySuspense';
import {
  AssetsLibrary,
  TemplateGallery,
  OnboardingModal,
  HelpModal,
  UserGuide,
  InteractiveTutorial,
  KeyboardShortcutsModal,
} from './components/react/common/LazyComponents';
import { useUnifiedAutoSave, useAutoSaveRestore } from './hooks/useUnifiedAutoSave';
// PHASE 3: KeyboardManager remplace useKeyboardShortcuts pour une gestion centralisée
// import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useTheme } from './hooks/useTheme';
import { useAppInitialization } from './hooks/useAppInitialization';
import { useAppStore } from './services/StateService';
import { createDefaultShape } from './types/ShapeTypes';
import { useAppCoordinatorOptional } from './hooks/useAppCoordinator';
import { useModals } from './hooks/useModals';
import { useDrawer } from './hooks/useDrawer';
import { useAccessibility } from './hooks/useAccessibility';
import { useMobileOptimization } from './hooks/useMobileOptimization';
import { useSafeZoneGuides } from './hooks/useSafeZoneGuides';
import { useErrorHandler } from './hooks/useErrorHandler';
import type { AssetItem } from './AssetsLibrary';
import { logger } from './utils/ProfessionalLogger';
import { EventBus } from './services/EventBus';
import { AuthProvider } from './components/auth/AuthProvider';
import { KeyboardManager } from './services/KeyboardManager';
import { AppRouter } from './routes/AppRouter';
import { MobileLayout } from './components/react/MobileLayout/MobileLayout';
import { useIsMobile } from './hooks/useMediaQuery';
import { StatusBar } from './components/react/common/StatusBar';
import { useServiceWorker } from './hooks/useServiceWorker';
import { useKonvaCacheCleanup, useSignedUrlCacheCleanup } from './hooks/useCacheCleanup';
import { usePerformanceMonitor } from './hooks/usePerformanceMonitor';
// P2.3 : useCloudManagerAutoSave retiré — doublait useUnifiedAutoSave (race).
// CSS centralisé dans `src/styles/main.css`, importé depuis `index.tsx`.
// Ne plus rajouter d'`import './styles/*.css'` ici (Phase 3.1).

/**
 * Composant interne qui utilise les hooks
 */
function AppContent(): React.ReactElement {
  const { isInitialized, error } = useAppInitialization();

  // Initialiser le thème (dark/light mode)
  useTheme();

  // Enregistrer le Service Worker pour le cache offline
  useServiceWorker();

  // Hooks pour remplacer les setInterval (remplace polling par subscriptions React)
  useKonvaCacheCleanup(isInitialized);
  useSignedUrlCacheCleanup(isInitialized);
  usePerformanceMonitor(isInitialized && (import.meta.env?.DEV ?? false));
  // useCloudManagerAutoSave retiré (P2.3) : useUnifiedAutoSave suffit.

  // Couche 7: Détection mobile pour le rendu conditionnel
  const isMobile = useIsMobile();
  // PR-07: Utiliser selection.primaryId au lieu de canvas.selectedNodeId
  const selectedNodeId = useAppStore(state => state.selection.primaryId);

  // Hooks d'auto-save unifié - sauvegarde localStorage + Supabase si projet ouvert
  const projectId = useAppStore(state => state.project.id);
  useUnifiedAutoSave({
    projectId: projectId || null,
    enabled: isInitialized,
    saveToCloud: true, // Sauvegarder dans Supabase si projectId disponible
  });
  useAutoSaveRestore();

  // FIX persistance : charger le projet depuis Supabase quand l'URL contient
  // `/editor/:projectId`. Sans ça, la route ignorait le paramètre → on rouvrait
  // un canvas vide alors que le projet était bien sauvegardé en base.
  const { projectId: urlProjectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const loadedProjectRef = useRef<string | null>(null);
  // A02 : état de chargement du projet par URL (loading / error) pour ne plus
  // laisser un écran vide silencieux si le projet est introuvable / refusé.
  const [projectLoadStatus, setProjectLoadStatus] = React.useState<'idle' | 'loading' | 'error'>(
    'idle'
  );
  const [loadAttempt, setLoadAttempt] = React.useState(0);

  useEffect(() => {
    if (!isInitialized || !urlProjectId) return;
    if (loadedProjectRef.current === urlProjectId) return; // déjà chargé
    loadedProjectRef.current = urlProjectId;

    let cancelled = false;
    setProjectLoadStatus('loading');
    (async () => {
      const { data, error } = await projectService.getProjectById(urlProjectId);
      if (cancelled) return;
      if (error || !data) {
        logger.warn('Chargement du projet depuis Supabase échoué', { error, urlProjectId });
        loadedProjectRef.current = null; // permettre une nouvelle tentative
        setProjectLoadStatus('error');
        return;
      }
      const store = useAppStore.getState();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cd = (data.canvas_data || {}) as any;
      // A22 : repartir d'un état PROPRE à chaque chargement, pour éviter que les
      // réglages du projet précédent (formes, couleur de fond, nom) ne « fuient »
      // dans le projet courant. On applique systématiquement une valeur (celle du
      // projet, ou le défaut) plutôt que de ne réinitialiser que si présent.
      store.clearSelection?.();
      store.loadShapes(Array.isArray(cd.shapes) ? cd.shapes : []);
      if (cd.canvas?.templateWidth && cd.canvas?.templateHeight) {
        store.setTemplateDimensions(cd.canvas.templateWidth, cd.canvas.templateHeight);
      }
      store.setBackgroundColor(cd.canvas?.backgroundColor || '#ffffff');
      const loadedName = data.name || 'Document sans titre';
      store.setProjectName(loadedName);
      // A22 : le topbar affiche `canvas.documentName` — il faut donc le synchroniser
      // aussi (auparavant seul project.name était mis à jour → nom périmé affiché).
      store.setDocumentName(loadedName);
      store.setProjectId(urlProjectId); // les sauvegardes mettront à jour CE projet
      setProjectLoadStatus('idle');
      logger.info(
        `✅ Projet ${urlProjectId} chargé depuis Supabase (${cd.shapes?.length ?? 0} shape(s))`
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [isInitialized, urlProjectId, loadAttempt]);

  // Après une sauvegarde sur un nouveau projet (id généré dans le store mais
  // absent de l'URL), on synchronise l'URL → /editor/:id, pour qu'un rechargement
  // recharge bien le projet depuis Supabase.
  useEffect(() => {
    if (!isInitialized || !projectId || urlProjectId) return;
    loadedProjectRef.current = projectId; // éviter un rechargement redondant
    navigate(`/editor/${projectId}`, { replace: true });
  }, [isInitialized, projectId, urlProjectId, navigate]);

  // Raccourcis clavier
  // PHASE 3: Initialiser KeyboardManager global (remplace useKeyboardShortcuts)
  useEffect(() => {
    if (!isInitialized) return;
    const keyboardManager = KeyboardManager.getInstance();
    logger.info('✅ KeyboardManager initialisé');

    return () => {
      keyboardManager.destroy();
    };
  }, [isInitialized]);

  // --- BLOC DE DÉMARRAGE : Vérification de l'initialisation ---
  useEffect(() => {
    if (!isInitialized) return;

    const initApp = async () => {
      logger.info('🚀 Initialisation du mode React-First...');

      // 1. Vérifier que AppService a bien le coordinator
      const { appService } = await import('./services/AppService');
      const coordinator = appService.getAppCoordinator();

      if (coordinator) {
        logger.info('✅ ReactFirstAppCoordinator connecté à AppService');
      } else {
        logger.warn('⚠️ ReactFirstAppCoordinator non disponible dans AppService');
      }

      // 2. Vérifier l'état initial du StateService (Zustand)
      const store = useAppStore.getState();

      // 3. Log de confirmation
      if (store.shapes.length === 0) {
        logger.info('📝 Projet vide prêt (aucune shape)');
      } else {
        logger.info(`📝 Projet chargé avec ${store.shapes.length} shape(s)`);
      }
    };

    initApp();
  }, [isInitialized]);
  // --- FIN DU BLOC ---

  // Toast pour les notifications de sauvegarde
  const { addToast: _addToast } = useToast();
  const saveStatus = useAppStore(state => state.save.status);

  // Actions du store
  const addShape = useAppStore(state => state.addShape);

  // Hooks pour gérer les modals et le drawer
  const modals = useModals();
  const drawer = useDrawer();

  // Hooks pour les services intégrés (Phase 2.4)
  useAccessibility();
  useMobileOptimization();
  useSafeZoneGuides();

  // Hook pour la gestion d'erreurs améliorée (Phase 2.5)
  const { handleAsync, notifyError } = useErrorHandler();

  // PHASE 4.3: Marquer l'onboarding comme vu
  const handleOnboardingClose = () => {
    localStorage.setItem('snapbooth-onboarding-seen', 'true');
    modals.setShowOnboarding(false);
  };

  // PHASE 4.3: Handler pour sélectionner un template depuis l'onboarding
  // Phase 2.5: Utilise ErrorNotificationService avec fallback
  const appCoordinator = useAppCoordinatorOptional();
  const handleSelectTemplate = useCallback(
    async (templateData: unknown) => {
      if (!appCoordinator || !templateData) {
        notifyError(new Error('AppCoordinator ou templateData non disponible'), {
          severity: 'medium',
          category: 'validation',
        });
        return;
      }

      await handleAsync(
        async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          appCoordinator.loadProject(templateData as any);
          handleOnboardingClose();
        },
        {
          errorMessage: 'Chargement du template',
          severity: 'high',
          category: 'user',
          fallback: {
            message: 'Impossible de charger le template. Voulez-vous réessayer ?',
            action: {
              label: 'Réessayer',
              handler: () => handleSelectTemplate(templateData),
            },
          },
        }
      );
    },
    [appCoordinator, handleAsync, notifyError, handleOnboardingClose]
  );

  // Handler pour ajouter un asset au canvas
  // Phase 2.5: Utilise ErrorNotificationService
  const handleSelectAsset = useCallback(
    async (asset: AssetItem) => {
      if (asset.type === 'image') {
        // CORRIGÉ: Créer une shape image avec l'URL de l'asset
        const shape = createDefaultShape('image', 200, 200);
        shape.src = asset.dataURL || asset.thumbnail || '';
        shape.width = asset.size.width;
        shape.height = asset.size.height;
        addShape(shape);
      } else if (asset.type === 'template') {
        // Pour les templates, charger le template complet depuis dataURL
        if (appCoordinator && asset.dataURL) {
          await handleAsync(
            async () => {
              const templateData = JSON.parse(asset.dataURL);
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              appCoordinator.loadProject(templateData as any);
            },
            {
              errorMessage: "Chargement du template depuis l'asset",
              severity: 'medium',
              category: 'user',
            }
          );
        }
      }
    },
    [addShape, appCoordinator, handleAsync]
  );

  // Notification de sauvegarde
  // Phase 2.5: Utilise ErrorNotificationService
  // Utilise une ref pour éviter de notifier les erreurs au montage initial
  const previousSaveStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (saveStatus === 'saved') {
      // Silencieux pour ne pas spammer
      previousSaveStatusRef.current = saveStatus;
    } else if (saveStatus === 'error') {
      // Ne notifier que si l'erreur vient d'un changement de statut (pas au montage initial)
      // ou si c'est une erreur persistante après plusieurs tentatives
      if (previousSaveStatusRef.current !== null && previousSaveStatusRef.current !== 'error') {
        notifyError(new Error('Erreur de sauvegarde automatique'), {
          severity: 'medium', // Réduit de 'high' à 'medium' pour les erreurs auto-save
          category: 'system',
          context: { action: 'autoSave' },
          retryable: true,
          retryAction: async () => {
            // La sauvegarde sera réessayée automatiquement par useUnifiedAutoSave
          },
        });
      }
      previousSaveStatusRef.current = saveStatus;
    } else {
      previousSaveStatusRef.current = saveStatus;
    }
  }, [saveStatus, notifyError]);

  // Écouter les événements pour ouvrir un drawer depuis d'autres composants
  useEffect(() => {
    const eventBus = EventBus.getInstance();

    const handleOpenDrawer = (event: {
      type: string;
      data?: { drawerType?: string };
      timestamp?: number;
    }) => {
      const drawerType = event.data?.drawerType;
      if (
        drawerType &&
        ['project', 'templates', 'layouts', 'text', 'elements', 'uploads'].includes(drawerType)
      ) {
        drawer.openDrawer(drawerType as typeof drawer.drawerType);
      }
    };

    eventBus.on('ui-action', handleOpenDrawer);

    return () => {
      eventBus.off('ui-action', handleOpenDrawer);
    };
  }, [drawer]);

  // Afficher un loader pendant l'initialisation
  if (!isInitialized) {
    return (
      <div className="loader-container">
        <div className="loader-spinner"></div>
        <h2 className="loader-title">Snapbooth</h2>
        <p className="loader-message">Initialisation de l'éditeur...</p>
        {error && <p className="loader-error">Erreur: {error.message}</p>}
      </div>
    );
  }

  // A02 : chargement d'un projet par URL (/editor/:id)
  if (urlProjectId && projectLoadStatus === 'loading') {
    return (
      <div className="loader-container">
        <div className="loader-spinner"></div>
        <p className="loader-message">Chargement du projet…</p>
      </div>
    );
  }

  // A02 : projet introuvable ou accès refusé (RLS masque les lignes non autorisées,
  // donc on ne peut pas distinguer « inexistant » de « non autorisé »).
  if (urlProjectId && projectLoadStatus === 'error') {
    return (
      <ErrorState
        icon="🗂️"
        title="Projet introuvable"
        message="Ce projet n'existe pas, a été supprimé, ou vous n'y avez pas accès."
        actions={[
          { label: 'Retour au dashboard', onClick: () => navigate('/dashboard'), primary: true },
          {
            label: 'Réessayer',
            onClick: () => {
              loadedProjectRef.current = null;
              setProjectLoadStatus('idle');
              setLoadAttempt(a => a + 1);
            },
          },
          { label: 'Nouveau projet', onClick: () => navigate('/editor') },
        ]}
      />
    );
  }

  return (
    <MobileLayout>
      <div className="snapbooth-pro-app">
        {/* Top Bar */}
        <SnapboothTopbar />

        {/* Main Body avec CSS Grid interne */}
        <div className={`snapbooth-main-body ${!drawer.isOpen ? 'drawer-closed' : ''}`}>
          {/* Slim Sidebar (Navigation Gauche) */}
          <ModernSidebar
            onDrawerToggle={(open: boolean) => {
              // onDrawerToggle est utilisé uniquement pour notifier l'état ouvert/fermé
              // La gestion réelle du drawerType est faite par onDrawerChange
              if (!open) {
                drawer.closeDrawer();
              }
              // Si open === true, onDrawerChange gère déjà l'ouverture avec le bon drawerType
            }}
            drawerOpen={drawer.isOpen}
            activeDrawer={drawer.drawerType}
            onDrawerChange={drawerType => {
              logger.debug('[App] onDrawerChange', { drawerType });
              if (drawerType) {
                logger.debug('[App] Ouverture du drawer', { drawerType });
                drawer.openDrawer(drawerType);
              } else {
                logger.debug('[App] Fermeture du drawer');
                drawer.closeDrawer();
              }
            }}
          />

          {/* Drawer Panel */}
          {drawer.drawerType && drawer.isOpen && (
            <div className={`snapbooth-drawer open`}>
              <DrawerContent type={drawer.drawerType} onClose={drawer.closeDrawer} />
            </div>
          )}

          {/* Canvas Area */}
          <main className="snapbooth-canvas-area">
            <CanvasArea />
          </main>

          {/* Right Sidebar (Properties) - Desktop seulement */}
          {!isMobile && <PropertiesPanel />}
        </div>

        {/* Bottom Bar */}
        <BottomBar />

        {/* UX: Status Bar (style Photoshop/Figma) */}
        <StatusBar />

        {/* Mobile: Bottom Sheet pour le panneau de propriétés */}
        {isMobile && selectedNodeId && (
          <div className="mobile-properties-drawer fixed bottom-0 left-0 right-0 w-full bg-white border-t border-gray-200 p-4 z-50 max-h-[70vh] overflow-y-auto">
            <div className="drawer-handle mx-auto w-12 h-1 bg-gray-300 rounded mb-4" />
            <PropertiesPanel />
          </div>
        )}

        {/* Modals - Lazy loaded pour améliorer les performances */}
        {modals.showAssetsLibrary && (
          <LazySuspense>
            <AssetsLibrary
              isOpen={modals.showAssetsLibrary}
              onClose={() => modals.setShowAssetsLibrary(false)}
              onSelectAsset={handleSelectAsset}
            />
          </LazySuspense>
        )}
        {modals.showTemplateGallery && (
          <LazySuspense>
            <TemplateGallery
              isOpen={modals.showTemplateGallery}
              onClose={() => modals.setShowTemplateGallery(false)}
            />
          </LazySuspense>
        )}
        {modals.showOnboarding && (
          <LazySuspense>
            <OnboardingModal
              isOpen={modals.showOnboarding}
              onClose={handleOnboardingClose}
              onSelectTemplate={handleSelectTemplate}
            />
          </LazySuspense>
        )}
        {modals.showHelpModal && (
          <LazySuspense>
            <HelpModal
              isOpen={modals.showHelpModal}
              onClose={() => {
                modals.setShowHelpModal(false);
                modals.setHelpModalContent(null);
              }}
              title={modals.helpModalContent?.title}
              message={modals.helpModalContent?.message}
            />
          </LazySuspense>
        )}
        {modals.showUserGuide && (
          <LazySuspense>
            <UserGuide />
          </LazySuspense>
        )}
        {modals.showTutorial && (
          <LazySuspense>
            <InteractiveTutorial
              onComplete={() => {
                localStorage.setItem('snapbooth-tutorial-seen', 'true');
                modals.setShowTutorial(false);
              }}
              onSkip={() => {
                localStorage.setItem('snapbooth-tutorial-seen', 'true');
                modals.setShowTutorial(false);
              }}
            />
          </LazySuspense>
        )}
        {modals.showKeyboardShortcuts && (
          <LazySuspense>
            <KeyboardShortcutsModal
              isOpen={modals.showKeyboardShortcuts}
              onClose={() => modals.setShowKeyboardShortcuts(false)}
            />
          </LazySuspense>
        )}
      </div>
    </MobileLayout>
  );
}

/**
 * Composant principal de l'application
 * Utilise une architecture déclarative avec React et react-konva
 */
export function App(): React.ReactElement {
  return <AppContent />;
}

/**
 * Composant wrapper pour le routing
 */
export function AppWithRouter(): React.ReactElement {
  return (
    <ErrorBoundary name="App">
      <AuthProvider>
        <ToastProvider>
          <ExportErrorListener />
          <AppRouter />
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
